import {Component, OnInit, OnDestroy, Optional} from '@angular/core';
import {Subscription} from 'rxjs';
import {DragulaService} from 'ng2-dragula';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {LayoutService} from '@ngmedax/layout';
import {
  MasterDataLayoutService,
  StandardFieldDefinition,
  GridRendererFieldEntry,
  ClientGridRendererConfig,
  MasterDataFieldType
} from '@ngmedax/master-data';

import {SettingsClientCustomService} from '../../services/settings-client-custom.service';
import {TRANSLATION_GRID_RENDERER_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ClientCustomFieldDefinition} from '../../../../types';


/**
 * Bag names for dragula.
 */
const DRAG_BAG_DISPLAY = 'grid-renderer-display';
const DRAG_BAG_DETAIL = 'grid-renderer-detail';

// hack to inject decorator declarations
export interface GridRendererEditorComponent extends Translatable {}

/**
 * Editor component for configuring the patient data column in grids.
 * Provides two drop zones (display field and detail line) with drag & drop.
 */
@Component({
  selector: 'app-grid-renderer-editor',
  templateUrl: './grid-renderer-editor.component.html',
  styleUrls: ['./grid-renderer-editor.component.css']
})
@Translatable({scope: TRANSLATION_GRID_RENDERER_SCOPE, keys: KEYS})
export class GridRendererEditorComponent implements OnInit, OnDestroy {
  /**
   * Fields configured for the display badge (linked patient name).
   */
  public displayFields: GridRendererFieldEntry[] = [];

  /**
   * Fields configured for the detail line below the badge.
   */
  public detailFields: GridRendererFieldEntry[] = [];

  /**
   * Standard field definitions.
   */
  public standardDefinitions: StandardFieldDefinition[] = [];

  /**
   * Custom field definitions.
   */
  public customDefinitions: ClientCustomFieldDefinition[] = [];

  /**
   * Is currently saving.
   */
  public isSaving = false;

  /**
   * Indicates unsaved changes.
   */
  public hasUnsavedChanges = false;

  /**
   * Search term for filtering available fields.
   */
  public fieldSearchTerm = '';

  /**
   * Subscriptions.
   */
  private subscriptions: Subscription[] = [];

  public constructor(
    @Optional() private translationService: TranslationService,
    private layoutService: LayoutService,
    private masterDataLayoutService: MasterDataLayoutService,
    private customFieldService: SettingsClientCustomService,
    private dragulaService: DragulaService
  ) {
    this.setupDragula();
  }

  public async ngOnInit() {
    await this.loadDefinitions();
    await this.loadConfig();
  }

  public ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions = [];

    if (this.dragulaService.find(DRAG_BAG_DISPLAY)) {
      this.dragulaService.destroy(DRAG_BAG_DISPLAY);
    }
    if (this.dragulaService.find(DRAG_BAG_DETAIL)) {
      this.dragulaService.destroy(DRAG_BAG_DETAIL);
    }
  }

  // =========================================================================
  // Available fields (not yet placed in either zone)
  // =========================================================================

  /**
   * Gets all field keys already placed in either zone.
   */
  private get placedKeys(): string[] {
    return [
      ...this.displayFields.map(f => f.fieldKey),
      ...this.detailFields.map(f => f.fieldKey)
    ];
  }

  /**
   * Gets available standard fields.
   */
  public get availableStandardFields(): StandardFieldDefinition[] {
    return this.standardDefinitions.filter(d => this.placedKeys.indexOf(d.key) === -1);
  }

  /**
   * Gets filtered standard fields based on search term.
   */
  public get filteredStandardFields(): StandardFieldDefinition[] {
    if (!this.fieldSearchTerm.trim()) {
      return this.availableStandardFields;
    }
    const term = this.fieldSearchTerm.toLowerCase();
    return this.availableStandardFields.filter(d => {
      const label = this._(KEYS.CRUD[d.labelKey] || d.labelKey).toLowerCase();
      return label.indexOf(term) !== -1 || d.key.toLowerCase().indexOf(term) !== -1;
    });
  }

  /**
   * Gets available custom fields.
   */
  public get availableCustomFields(): ClientCustomFieldDefinition[] {
    return this.customDefinitions.filter(d => this.placedKeys.indexOf(d.key) === -1);
  }

  /**
   * Gets filtered custom fields based on search term.
   */
  public get filteredCustomFields(): ClientCustomFieldDefinition[] {
    if (!this.fieldSearchTerm.trim()) {
      return this.availableCustomFields;
    }
    const term = this.fieldSearchTerm.toLowerCase();
    return this.availableCustomFields.filter(d =>
      d.label.toLowerCase().indexOf(term) !== -1 || d.key.toLowerCase().indexOf(term) !== -1
    );
  }

  // =========================================================================
  // Field labels
  // =========================================================================

  /**
   * Gets the display label for a field entry.
   */
  public getFieldLabel(entry: GridRendererFieldEntry): string {
    if (entry.fieldType === 'standard') {
      const def = this.standardDefinitions.find(d => d.key === entry.fieldKey);
      return def ? this._(KEYS.CRUD[def.labelKey] || def.labelKey) : entry.fieldKey;
    }

    const def = this.customDefinitions.find(d => d.key === entry.fieldKey);
    return def ? def.label : entry.fieldKey;
  }

  // =========================================================================
  // Actions
  // =========================================================================

  /**
   * Adds a field to the display zone.
   */
  public addToDisplay(fieldKey: string, fieldType: MasterDataFieldType) {
    this.displayFields.push({fieldKey, fieldType});
    this.markDirty();
  }

  /**
   * Adds a field to the detail zone.
   */
  public addToDetail(fieldKey: string, fieldType: MasterDataFieldType) {
    this.detailFields.push({fieldKey, fieldType});
    this.markDirty();
  }

  /**
   * Removes a field from the display zone.
   */
  public removeFromDisplay(index: number) {
    this.displayFields.splice(index, 1);
    this.markDirty();
  }

  /**
   * Removes a field from the detail zone.
   */
  public removeFromDetail(index: number) {
    this.detailFields.splice(index, 1);
    this.markDirty();
  }

  /**
   * Updates the label for a display field.
   */
  public onDisplayLabelChange(index: number, value: string) {
    this.displayFields[index].label = value || undefined;
    this.markDirty();
  }

  /**
   * Updates the label for a detail field.
   */
  public onDetailLabelChange(index: number, value: string) {
    this.detailFields[index].label = value || undefined;
    this.markDirty();
  }

  // =========================================================================
  // Preview
  // =========================================================================

  /**
   * Gets the preview display text.
   */
  public get previewDisplayText(): string {
    if (this.displayFields.length === 0) {
      return 'Max Mustermann';
    }
    return this.displayFields.map(f => this.getPreviewValue(f)).join(' ');
  }

  /**
   * Gets the preview detail text.
   */
  public get previewDetailText(): string {
    if (this.detailFields.length === 0) {
      return '';
    }
    return this.detailFields.map(f => {
      const val = this.getPreviewValue(f);
      if (f.label) {
        return `${f.label} ${val}`;
      }
      return val;
    }).join(' – ');
  }

  /**
   * Returns a preview value for a given field.
   */
  private getPreviewValue(entry: GridRendererFieldEntry): string {
    const previewValues: {[key: string]: string} = {
      firstName: 'Max',
      lastName: 'Mustermann',
      gender: 'm',
      birthDate: '01.01.1990',
      customerNr: '12345',
      caseNr: 'F-001',
      email: 'max@example.de',
      zipCode: '12345',
      city: 'Berlin',
      street: 'Musterstr.',
      streetNr: '1',
      location: 'Station A',
      pseudoId: 'PSE-001',
      status: 'aktiv',
      locale: 'de_DE'
    };

    if (entry.fieldType === 'standard' && previewValues[entry.fieldKey]) {
      return previewValues[entry.fieldKey];
    }

    if (entry.fieldType === 'custom') {
      const def = this.customDefinitions.find(d => d.key === entry.fieldKey);
      return def ? `[${def.label}]` : `[${entry.fieldKey}]`;
    }

    return `[${entry.fieldKey}]`;
  }

  // =========================================================================
  // Save / Reset / Load Default
  // =========================================================================

  /**
   * Saves the configuration.
   */
  public async onSave() {
    this.isSaving = true;

    try {
      const config: ClientGridRendererConfig = {
        displayFields: this.displayFields.map(f => ({...f})),
        detailFields: this.detailFields.map(f => ({...f}))
      };
      await this.masterDataLayoutService.saveGridRendererConfig(config);
      this.hasUnsavedChanges = false;
      alert(this._(KEYS.GRID_RENDERER.SAVE_SUCCESS));
    } catch (error) {
      console.error('Failed to save grid renderer config', error);
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Resets all fields (clears both zones).
   */
  public onReset() {
    this.displayFields = [];
    this.detailFields = [];
    this.markDirty();
  }

  /**
   * Loads the default configuration.
   */
  public onLoadDefault() {
    const defaults = this.masterDataLayoutService.getDefaultGridRendererConfig();
    this.displayFields = defaults.displayFields;
    this.detailFields = defaults.detailFields;
    this.markDirty();
  }

  // =========================================================================
  // Private helpers
  // =========================================================================

  /**
   * Sets up dragula for drag & drop reordering inside zones.
   */
  private setupDragula() {
    // clean up existing bags
    if (this.dragulaService.find(DRAG_BAG_DISPLAY)) {
      this.dragulaService.destroy(DRAG_BAG_DISPLAY);
    }
    if (this.dragulaService.find(DRAG_BAG_DETAIL)) {
      this.dragulaService.destroy(DRAG_BAG_DETAIL);
    }

    // display zone: reorder only
    this.dragulaService.createGroup(DRAG_BAG_DISPLAY, {
      moves: (el, container, handle) => {
        return handle && (handle.classList.contains('drag-handle') ||
          (handle.parentElement && handle.parentElement.classList.contains('drag-handle')));
      }
    });

    // detail zone: reorder only
    this.dragulaService.createGroup(DRAG_BAG_DETAIL, {
      moves: (el, container, handle) => {
        return handle && (handle.classList.contains('drag-handle') ||
          (handle.parentElement && handle.parentElement.classList.contains('drag-handle')));
      }
    });

    this.subscriptions.push(
      this.dragulaService.dropModel(DRAG_BAG_DISPLAY).subscribe(() => {
        this.markDirty();
      }),
      this.dragulaService.dropModel(DRAG_BAG_DETAIL).subscribe(() => {
        this.markDirty();
      })
    );
  }

  /**
   * Loads field definitions.
   */
  private async loadDefinitions() {
    try {
      this.standardDefinitions = this.masterDataLayoutService.getStandardFieldDefinitions();

      const result = await this.customFieldService.loadCustomFields();
      this.customDefinitions = result.rows;
    } catch (error) {
      console.error('Failed to load field definitions', error);
      this.customDefinitions = [];
    }
  }

  /**
   * Loads the grid renderer config from settings.
   */
  private async loadConfig() {
    try {
      const config = await this.masterDataLayoutService.loadGridRendererConfig();
      const effective = this.masterDataLayoutService.getEffectiveGridRendererConfig(config);
      this.displayFields = effective.displayFields.map(f => ({...f}));
      this.detailFields = effective.detailFields.map(f => ({...f}));
    } catch (error) {
      console.error('Failed to load grid renderer config', error);
      const defaults = this.masterDataLayoutService.getDefaultGridRendererConfig();
      this.displayFields = defaults.displayFields;
      this.detailFields = defaults.detailFields;
    }
  }

  /**
   * Marks configuration as dirty.
   */
  private markDirty() {
    this.hasUnsavedChanges = true;
  }
}
